import axios from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001'

const api = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Single in-flight refresh shared by every caller (interceptor, store, hooks)
let refreshPromise = null

const AUTH_PATHS = ['/admin/login', '/admin/register', '/admin/refresh-token']

const clearSession = () => {
  localStorage.removeItem('agent_token')
  localStorage.removeItem('agent_refresh_token')
  localStorage.removeItem('agent_user')
  delete api.defaults.headers.common['Authorization']

  if (window.useAuthStore) {
    window.useAuthStore.setState({
      agent: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false
    })
  }
}

// Exchanges the stored refresh token for a new access token.
// Only a 401 from the server (invalid/expired refresh token) ends the session;
// network errors and 5xx leave the session intact so a retry can succeed.
export const refreshAccessToken = () => {
  if (refreshPromise) return refreshPromise

  const storedRefresh = localStorage.getItem('agent_refresh_token')
  if (!storedRefresh) {
    clearSession()
    return Promise.reject(new Error('No refresh token available'))
  }

  refreshPromise = axios
    .post(`${API_BASE_URL}/api/admin/refresh-token`, { refreshToken: storedRefresh }, { timeout: 10000 })
    .then((response) => {
      const { token, refreshToken, agent } = response.data

      localStorage.setItem('agent_token', token)
      if (refreshToken) localStorage.setItem('agent_refresh_token', refreshToken)
      if (agent) localStorage.setItem('agent_user', JSON.stringify(agent))
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`

      if (window.useAuthStore) {
        window.useAuthStore.setState((state) => ({
          token,
          refreshToken: refreshToken || state.refreshToken,
          agent: agent ? { ...state.agent, ...agent } : state.agent,
          isAuthenticated: true
        }))
      }

      return token
    })
    .catch((error) => {
      if (error.response?.status === 401) clearSession()
      throw error
    })
    .finally(() => {
      refreshPromise = null
    })

  return refreshPromise
}

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add timestamp to prevent caching
    config.params = {
      ...config.params,
      _t: Date.now()
    }

    // Add authorization header if token exists
    const token = localStorage.getItem('agent_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor with token refresh logic
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config
    const isAuthCall = AUTH_PATHS.some((path) => originalRequest?.url?.includes(path))

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry && !isAuthCall) {
      originalRequest._retry = true

      try {
        const newToken = await refreshAccessToken()
        originalRequest.headers.Authorization = `Bearer ${newToken}`
        return api(originalRequest)
      } catch (refreshError) {
        // clearSession() already flipped isAuthenticated, so ProtectedRoute sends the agent
        // to /signin and remembers the page (a hard redirect here would lose ?session=<id>)
        return Promise.reject(refreshError)
      }
    } else if (error.response?.status === 403) {
      console.error('Access forbidden')
    } else if (error.response?.status >= 500) {
      console.error('Server error:', error.response.data)
    }

    return Promise.reject(error)
  }
)

export default api
